import { useMemo } from "react";

// value: { moduleKey: [actions] }. onChange receives the updated map.
export default function PermissionMatrix({ modules, actions, value, onChange, readOnly = false }) {
  const grouped = useMemo(() => {
    const g = {};
    (modules || []).forEach((m) => { (g[m.group] = g[m.group] || []).push(m); });
    return g;
  }, [modules]);

  const has = (mod, act) => Array.isArray(value?.[mod]) && value[mod].includes(act);

  const toggle = (mod, act) => {
    if (readOnly) return;
    const cur = new Set(value?.[mod] || []);
    cur.has(act) ? cur.delete(act) : cur.add(act);
    const next = { ...(value || {}) };
    const arr = actions.filter((a) => cur.has(a));
    if (arr.length) next[mod] = arr; else delete next[mod];
    onChange(next);
  };

  const toggleAll = (mod) => {
    if (readOnly) return;
    const next = { ...(value || {}) };
    const full = (value?.[mod] || []).length === actions.length;
    if (full) delete next[mod]; else next[mod] = [...actions];
    onChange(next);
  };

  return (
    <div className="space-y-6" data-testid="permission-matrix">
      {Object.entries(grouped).map(([group, mods]) => (
        <div key={group}>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-gold-dark">{group}</p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy/[0.03] text-left">
                  <th className="px-3 py-2 font-semibold text-navy">Module</th>
                  {actions.map((a) => <th key={a} className="px-2 py-2 text-center text-[11px] font-semibold uppercase text-charcoal/70">{a}</th>)}
                  <th className="px-2 py-2 text-center text-[11px] font-semibold uppercase text-charcoal/70">All</th>
                </tr>
              </thead>
              <tbody>
                {mods.map((m) => (
                  <tr key={m.key} className="border-t border-border/60" data-testid={`perm-row-${m.key}`}>
                    <td className="px-3 py-2 text-charcoal">{m.label}</td>
                    {actions.map((a) => (
                      <td key={a} className="px-2 py-2 text-center">
                        <input type="checkbox" disabled={readOnly} checked={has(m.key, a)} onChange={() => toggle(m.key, a)}
                          className="h-4 w-4 accent-navy" data-testid={`perm-${m.key}-${a}`} />
                      </td>
                    ))}
                    <td className="px-2 py-2 text-center">
                      <input type="checkbox" disabled={readOnly}
                        checked={(value?.[m.key] || []).length === actions.length}
                        onChange={() => toggleAll(m.key)} className="h-4 w-4 accent-gold" data-testid={`perm-${m.key}-all`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
